import { trackMixPanelEvent } from './ApiService';

// Function to organize flat field list into hierarchical structure with sections
const organizeFieldsIntoHierarchy = (fields) => {
    let organizedFields = [];
    let currentSection = null;
    
    fields.forEach(field => {
      if (field.type === 'Section') {
        // Start a new section
        field.elements = []; // Initialize elements array for the section
        organizedFields.push(field);
        currentSection = field;
      } else if (currentSection) {
        // Add this field to the current section
        currentSection.elements.push(field);
      } else {
        // No active section, add to top level
        organizedFields.push(field);
      }
    });
    
    // Ensure no empty sections by adding a default Label if needed
    organizedFields.forEach(field => {
      if (field.type === 'Section' && field.elements.length === 0) {
        field.elements.push({
          type: 'Label',
          label: 'Additional Information',
          data_name: 'additional_information'
        });
      }
    });
    
    return organizedFields;
  };
  
  const generateFulcrumAppJSON = (formFields, formName = "Auto-Generated Form", formDescription = "Automatically generated from form image") => {
    // Create a unique resource ID
    const generateId = () => Math.random().toString(36).substring(2, 15) + 
                           Math.random().toString(36).substring(2, 15);
    
    // Track used keys to avoid duplicates
    const usedKeys = new Set();
    
    // Generate a unique key for each field (4 character hex)
    const generateKey = () => {
      let key;
      do {
        key = Math.floor((1 + Math.random()) * 0x10000)
              .toString(16)
              .substring(1);
      } while (usedKeys.has(key));
      
      usedKeys.add(key);
      return key;
    };
    
    // Base structure for the minimal Fulcrum app - only include what's needed
    // Now wrapped with a "form" object
    const fulcrumApp = {
      "form": {
        "name": formName,
        "description": formDescription,
        "version": 1,
        "elements": [],
        "schema_version": "v5"
      }
    };
    
    // Function to process a field and its possible children
    const processField = (field, parentArray) => {
      const label = field.label || "Unnamed Field";
      const dataName = label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      
      // Use existing key if it exists and isn't a duplicate, otherwise generate a new one
      const key = field.key && !usedKeys.has(field.key) 
        ? field.key 
        : generateKey();
      
      // Track this key as used
      usedKeys.add(key);
      
      // Base field properties that most field types have
      const baseField = {
        "key": key,
        "label": label,
        "description": null,
        "required": false,
        "disabled": false,
        "hidden": false,
        "data_name": dataName,
        "default_value": null,
        "visible_conditions_type": null,
        "visible_conditions_behavior": "clear",
        "visible_conditions": null,
        "required_conditions_type": null,
        "required_conditions": null,
        "default_previous_value": false
      };
      
      let fieldConfig = null;
      
      // Configure field based on type
      switch (field.type) {
        case "Section":
          fieldConfig = {
            ...baseField,
            "type": "Section",
            "display": "inline",
            "elements": []
          };
          
          // Process children if they exist
          if (Array.isArray(field.elements) && field.elements.length > 0) {
            field.elements.forEach(childField => {
              processField(childField, fieldConfig.elements);
            });
          } else {
            // Add a default label to avoid empty elements array
            const defaultLabelKey = generateKey();
            fieldConfig.elements.push({
              "key": defaultLabelKey,
              "type": "Label",
              "label": "Additional Information",
              "description": null,
              "required": false,
              "disabled": false,
              "hidden": false,
              "data_name": "additional_information"
            });
          }
          break;
          
        case "TextField":
          fieldConfig = {
            ...baseField,
            "type": "TextField",
            "ai_prompt": null,
            "numeric": false,
            "pattern": null,
            "pattern_description": null,
            "min_length": null,
            "max_length": null
          };
          break;
          
        case "NumericField":
          fieldConfig = {
            ...baseField,
            "type": "TextField", // Fulcrum uses TextField with numeric=true
            "ai_prompt": null,
            "numeric": true,
            "format": "decimal",
            "min": null,
            "max": null,
            "min_length": null,
            "max_length": null
          };
          break;
          
        case "YesNoField":
          fieldConfig = {
            ...baseField,
            "type": "YesNoField",
            "ai_prompt": null,
            "positive": {
              "label": "Yes",
              "value": "yes"
            },
            "negative": {
              "label": "No",
              "value": "no"
            },
            "neutral": {
              "label": "N/A",
              "value": "n/a"
            },
            "neutral_enabled": false
          };
          break;
          
        case "DateTimeField":
          fieldConfig = {
            ...baseField,
            "type": "DateTimeField",
            "ai_prompt": null
          };
          break;
          
        case "TimeField":
          fieldConfig = {
            ...baseField,
            "type": "TimeField",
            "ai_prompt": null
          };
          break;
          
       case "ChoiceField":
        fieldConfig = {
          ...baseField,
          "type": "ChoiceField",
          "ai_prompt": null,
          "multiple": false,
          "allow_other": false,
          "choices": field.choices && field.choices.length > 0 
            ? field.choices.map(choice => ({
                "label": choice.label || choice,
                "value": choice.value || choice.label || choice
              }))
            : [
                { "label": "Option 1", "value": "option_1" },
                { "label": "Option 2", "value": "option_2" },
                { "label": "Option 3", "value": "option_3" }
              ]
        };
        break;
        
      case "MultiChoiceField":
        fieldConfig = {
          ...baseField,
          "type": "ChoiceField", // Fulcrum uses ChoiceField with multiple=true
          "ai_prompt": null,
          "multiple": true,
          "allow_other": true,
          "choices": field.choices && field.choices.length > 0 
            ? field.choices.map(choice => ({
                "label": choice.label || choice,
                "value": choice.value || choice.label || choice
              }))
            : [
                { "label": "Option 1", "value": "option_1" },
                { "label": "Option 2", "value": "option_2" },
                { "label": "Option 3", "value": "option_3" }
              ],
          "min_length": null,
          "max_length": null
        };
        break;
          
        case "Label":
          fieldConfig = {
            ...baseField,
            "type": "Label"
          };
          break;
          
        case "AddressField":
          fieldConfig = {
            ...baseField,
            "type": "AddressField",
            "ai_prompt": null,
            "auto_populate": true
          };
          break;
          
        case "SignatureField":
          fieldConfig = {
            ...baseField,
            "type": "SignatureField",
            "agreement_text": ""
          };
          break;
          
    
          
        case "PhotoField":
          fieldConfig = {
            ...baseField,
            "type": "PhotoField",
            "min_length": null,
            "max_length": null,
            "annotations_enabled": false,
            "deidentification_enabled": true,
            "timestamp_enabled": false,
            "latlongstamp_enabled": false
          };
          break;
          


        
          

          
        default:
          // For any other field types, use TextField as fallback
          fieldConfig = {
            ...baseField,
            "type": "TextField",
            "ai_prompt": null,
            "numeric": false,
            "pattern": null,
            "pattern_description": null,
            "min_length": null,
            "max_length": null
          };
          break;
      }
      
      // Add the configured field to the parent array
      if (fieldConfig) {
        parentArray.push(fieldConfig);
      }
    };
    
    // Process each top-level field and its children
    formFields.forEach(field => {
      processField(field, fulcrumApp.form.elements);
    });
    
    return fulcrumApp;
  };
  
  // Function to download the generated Fulcrum app JSON
  const downloadFulcrumAppJSON = (formFields, formName, formDescription) => {
    try {
      const fulcrumApp = generateFulcrumAppJSON(formFields, formName, formDescription);
      const dataStr = JSON.stringify(fulcrumApp, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      
      const exportFileDefaultName = 'fulcrum_app.json';
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
      
      trackMixPanelEvent('Instaform Form Created', {
        type: 'download',
        result: 'success', 
        form_id: null,
        organization_id: null,
        error: null
      })
      
      return fulcrumApp;
    } catch (err) {
      console.error(err);
      trackMixPanelEvent('Instaform Form Created', {
        type: 'download',
        result: 'failure', 
        form_id: null,
        organization_id: null,
        error: err.message
      })
    }
  };
  
  export { generateFulcrumAppJSON, downloadFulcrumAppJSON, organizeFieldsIntoHierarchy };